import { useEffect, useRef, useState } from "react";
import { api } from "./client";
import type { NotificationEvent, Shot } from "./types";
import { wsBus } from "./wsBus";

/**
 * Subscribes to {@code /ws/notifications}. Auto-reconnects with exponential
 * backoff (capped at 30s). On every reconnect, calls
 * {@code /api/notifications/since?ts=<lastSeen>} so events that happened
 * during the gap are replayed as synthetic {@code image.uploaded} events. The
 * "ts" is also bumped on each event so the next replay window starts there.
 *
 * Events are fanned out via {@link wsBus} so every screen can subscribe
 * independently without the App.tsx component prop-drilling them in.
 */
export function useWebSocket(): { connected: boolean } {
  const [connected, setConnected] = useState(false);
  const lastTsRef = useRef<string>(new Date().toISOString());

  useEffect(() => {
    let stopped = false;
    let attempt = 0;
    let ws: WebSocket | null = null;
    let reconnectTimer: number | undefined;

    const replayMissed = async () => {
      try {
        const since = lastTsRef.current;
        const missed: Shot[] = await api.notificationsSince(since);
        for (const s of missed) {
          wsBus.emit({
            type: "image.uploaded",
            imageId: s.imageId,
            cameraId: s.cameraId,
            timestamp: s.uploadedAt,
          });
          if (s.uploadedAt > lastTsRef.current) lastTsRef.current = s.uploadedAt;
        }
      } catch {
        // Backend may be down — the reconnect loop will keep trying.
      }
    };

    const connect = () => {
      if (stopped) return;
      const scheme = location.protocol === "https:" ? "wss" : "ws";
      ws = new WebSocket(`${scheme}://${location.host}/ws/notifications`);
      ws.onopen = () => {
        attempt = 0;
        setConnected(true);
        void replayMissed();
      };
      ws.onmessage = (msg) => {
        try {
          const evt = JSON.parse(msg.data) as NotificationEvent;
          if (evt.timestamp > lastTsRef.current) lastTsRef.current = evt.timestamp;
          wsBus.emit(evt);
        } catch {
          // Ignore non-JSON frames.
        }
      };
      ws.onclose = () => {
        setConnected(false);
        if (stopped) return;
        const delay = Math.min(30_000, 500 * 2 ** attempt++);
        reconnectTimer = window.setTimeout(connect, delay);
      };
      ws.onerror = () => {
        // close handler will run next and schedule a retry
      };
    };

    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, []);

  return { connected };
}
